import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Button,
} from "@mui/material";
import { Timestamp } from "firebase/firestore";
import useFirestore from "../../../../config/fireBaseHook";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

const Edit = ({ open, onClose, programDeliveryDetail, onRecordUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const { updateData: updateFirestoreData, deleteData: deleteFirestoreData } =
    useFirestore("ProgramDelivery");

  // Fetch PathwaysPrograms and Sites data for dropdowns
  const { data: pathwaysData } = useFirestore(
    "PathwaysPrograms",
    "PathwayName",
    "asc"
  );
  const { data: sitesData } = useFirestore("Sites", "SiteID", "asc");

  const initialFormData = {
    CCcode: "",
    CoordinatorID: "",
    HighSchool: "",
    PathwayID: "",
    ProgramDeliveryID: "",
    SiteOfDelivery: "",
    SOD_NameForApp: "",
    BegDate: "",
    EndDate: "",
    NonTPconsortium: -1,
    Notes: "",
    Purge_Ts: "",
    SC_SoD_Inst_Id_Nb: "",
    SiteID: "",
    SODcode: "",
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (programDeliveryDetail) {
      const newFormData = { ...initialFormData };

      // Merge programDeliveryDetail into newFormData
      for (const key in programDeliveryDetail) {
        if (
          Object.prototype.hasOwnProperty.call(programDeliveryDetail, key) &&
          Object.prototype.hasOwnProperty.call(newFormData, key)
        ) {
          newFormData[key] = programDeliveryDetail[key];
        }
      }

      // Format Firestore Timestamps to 'YYYY-MM-DD' strings for date input fields
      const dateFields = ["BegDate", "EndDate"];
      dateFields.forEach((field) => {
        if (newFormData[field]) {
          try {
            let date = null;

            // Handle Firestore Timestamp
            if (
              typeof newFormData[field] === "object" &&
              newFormData[field].toDate
            ) {
              date = newFormData[field].toDate();
            }
            // Handle regular Date object
            else if (newFormData[field] instanceof Date) {
              date = newFormData[field];
            }
            // Handle date string
            else if (
              typeof newFormData[field] === "string" &&
              newFormData[field]
            ) {
              date = new Date(newFormData[field]);
            }
            // Handle timestamp number
            else if (typeof newFormData[field] === "number") {
              date = new Date(newFormData[field]);
            }

            if (date && !isNaN(date.getTime())) {
              newFormData[field] = date.toISOString().split("T")[0];
            } else {
              newFormData[field] = "";
            }
          } catch (error) {
            console.error(`Error formatting ${field}:`, error);
            newFormData[field] = "";
          }
        } else {
          newFormData[field] = "";
        }
      });

      // Add the document ID
      newFormData.id = programDeliveryDetail.id;

      setFormData(newFormData);
    }
  }, [programDeliveryDetail]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSiteOfDeliveryChange = (e) => {
    const selectedSiteId = e.target.value;
    const selectedSite = sitesData?.find(
      (site) => site.SiteID === selectedSiteId
    );

    setFormData((prevData) => ({
      ...prevData,
      SiteOfDelivery:
        selectedSite?.SiteName || selectedSite?.SiteNameShort || "",
      SiteID: selectedSiteId,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setSnackbarMessage("");
    try {
      const { id, ...dataToUpdate } = formData;
      if (!id) {
        throw new Error("Record ID is missing. Cannot update.");
      }

      // Convert date strings to Firestore Timestamps
      if (dataToUpdate.BegDate && typeof dataToUpdate.BegDate === "string") {
        try {
          const begDate = new Date(dataToUpdate.BegDate);
          if (!isNaN(begDate.getTime())) {
            dataToUpdate.BegDate = Timestamp.fromDate(begDate);
          } else {
            dataToUpdate.BegDate = null;
          }
        } catch (error) {
          console.error("Error parsing begin date:", error);
          dataToUpdate.BegDate = null;
        }
      }

      if (dataToUpdate.EndDate && typeof dataToUpdate.EndDate === "string") {
        try {
          const endDate = new Date(dataToUpdate.EndDate);
          if (!isNaN(endDate.getTime())) {
            dataToUpdate.EndDate = Timestamp.fromDate(endDate);
          } else {
            dataToUpdate.EndDate = null;
          }
        } catch (error) {
          console.error("Error parsing end date:", error);
          dataToUpdate.EndDate = null;
        }
      }

      // Convert numeric fields
      if (dataToUpdate.MaxEnrollment) {
        dataToUpdate.MaxEnrollment = parseInt(dataToUpdate.MaxEnrollment) || 0;
      }
      if (dataToUpdate.CurrentEnrollment) {
        dataToUpdate.CurrentEnrollment =
          parseInt(dataToUpdate.CurrentEnrollment) || 0;
      }

      await updateFirestoreData(id, dataToUpdate);
      setSnackbarMessage("Record updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      if (onRecordUpdated) onRecordUpdated();
    } catch (error) {
      console.error("Error updating record:", error);
      setSnackbarMessage(`Error updating record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setSnackbarMessage("");
    try {
      if (!formData.id) {
        throw new Error("Record ID is missing. Cannot delete.");
      }
      await deleteFirestoreData(formData.id);
      setSnackbarMessage("Record deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setConfirmDeleteOpen(false);
      if (onRecordUpdated) onRecordUpdated();
      onClose();
    } catch (error) {
      console.error("Error deleting record:", error);
      setSnackbarMessage(`Error deleting record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setConfirmDeleteOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDeleteClick = () => {
    setConfirmDeleteOpen(true);
  };

  const handleDeleteCancel = () => {
    setConfirmDeleteOpen(false);
  };

  // The X (and backdrop/Escape, which share the same Dialog onClose) always
  // asks for confirmation first rather than closing immediately, so an
  // in-progress edit isn't lost by an accidental click.
  const handleCloseAttempt = () => {
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseAttempt} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleCloseAttempt}>
          Edit Program Delivery Record
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="CC Code"
                name="CCcode"
                value={formData.CCcode}
                onChange={handleChange}
                placeholder="e.g. 001, 002, 003"
                helperText="Include leading zeros (e.g., 001)"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Coordinator ID"
                name="CoordinatorID"
                type="number"
                value={formData.CoordinatorID}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="High School"
                name="HighSchool"
                value={formData.HighSchool}
                onChange={handleChange}
                placeholder="e.g. 001, 002, 003"
                helperText="Include leading zeros (e.g., 001)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Pathway ID</InputLabel>
                <Select
                  name="PathwayID"
                  value={formData.PathwayID}
                  onChange={handleChange}
                  label="Pathway ID"
                >
                  <MenuItem value="">
                    <em>Select Pathway</em>
                  </MenuItem>
                  {pathwaysData?.map((pathway) => (
                    <MenuItem
                      key={pathway.id}
                      value={pathway.PathwayID || pathway.id}
                    >
                      {pathway.PathwayName} ({pathway.PathwayID || pathway.id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Program Delivery ID"
                name="ProgramDeliveryID"
                value={formData.ProgramDeliveryID}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Site of Delivery</InputLabel>
                <Select
                  name="SiteOfDelivery"
                  value={formData.SiteID} // Use SiteID as the value for the dropdown
                  onChange={handleSiteOfDeliveryChange}
                  label="Site of Delivery"
                >
                  <MenuItem value="">
                    <em>Select Site</em>
                  </MenuItem>
                  {sitesData?.map((site) => (
                    <MenuItem key={site.id} value={site.SiteID}>
                      {site.SiteName} ({site.SiteID})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SOD Name for App"
                name="SOD_NameForApp"
                value={formData.SOD_NameForApp}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Begin Date"
                name="BegDate"
                type="date"
                value={formData.BegDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                name="EndDate"
                type="date"
                value={formData.EndDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Non-TP Consortium</InputLabel>
                <Select
                  name="NonTPconsortium"
                  value={formData.NonTPconsortium}
                  onChange={handleChange}
                  label="Non-TP Consortium"
                >
                  <MenuItem value={-1}>Yes</MenuItem>
                  <MenuItem value={0}>No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Site ID"
                name="SiteID"
                value={formData.SiteID}
                disabled
                helperText="Auto-populated when Site of Delivery is selected"
                InputProps={{
                  style: { backgroundColor: "#f5f5f5" },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SOD Code"
                name="SODcode"
                value={formData.SODcode}
                onChange={handleChange}
                placeholder="e.g. 001, 002, 003"
                helperText="Include leading zeros (e.g., 001)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SC SoD Inst ID Nb"
                name="SC_SoD_Inst_Id_Nb"
                type="number"
                value={formData.SC_SoD_Inst_Id_Nb}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="Notes"
                value={formData.Notes}
                onChange={handleChange}
                multiline
                rows={4}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions
          onSave={handleSubmit}
          saving={loading}
          onDelete={handleDeleteClick}
          deleting={loading}
        />
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDelete}
        deleting={loading}
        message="Are you sure you want to delete this program delivery record? This action cannot be undone."
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Edit;
